// GroupTreeNode — 사이드바 트리의 재귀 노드 컴포넌트 (U37 행 영역 분할 재작성).
//
// 행 좌→우 영역:
//  1. [드래그 핸들]  GripVertical — hover 시 노출
//  2. [토글]        ChevronRight/ChevronDown — 자식 있을 때만
//  3. [그룹 아이콘]  Folder/FolderOpen (lucide)
//  4. [이름]         flex-grow, ellipsis. 더블클릭 → 인라인 편집. F2 단축키
//  5. [카운트 뱃지]  자식 수. hover 시에만 노출 (depth >= 2 또는 좁은 상황)
//  6. [+ 버튼]       IconButton(Plus) — hover 시 노출. 클릭 → 하위 그룹 인라인 추가
//  7. [⋯ 메뉴]       DropdownMenu(MoreHorizontal) — 이름 변경(F2)·하위 추가(N)·삭제(Del)
//
// DnD 기능:
//  - useDraggable: 행 전체가 드래그 대상
//  - useDroppable: 드롭 수용 대상
//  - hover 800ms 자동 펼침 콜백 등록 유지
//
// 키보드 단축키 (행 focus 시):
//  - F2  → 인라인 이름 편집 시작
//  - N   → 하위 그룹 추가 (인라인 입력 + 자동 펼침)
//  - Del → 그룹 삭제 (Undo 토스트)
//
// 아이콘: lucide-react (U34)

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  GripVertical,
  MoreHorizontal,
  Plus,
  Trash2,
  Pencil,
} from 'lucide-react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { GroupTreeNode as GroupTreeNodeType } from '../api/groupsTree';
import { useDndExt } from './DndProvider';
import type { DndDraggableData, DndDroppableData } from '../utils/dndHelpers';
import { DropdownMenu, DropdownItem, DropdownSeparator } from '../ui/DropdownMenu';
import { IconButton } from '../ui/IconButton';
import { Tooltip } from '../ui/Tooltip';
import { useToast } from '../ui/Toast';
import { useUpdateGroup, useCreateGroup, useDeleteGroup } from '../hooks/useGroups';
import { InlineEditable } from './InlineEditable';

// ─── 인터페이스 ───────────────────────────────────────────────

interface GroupTreeNodeProps {
  /** 렌더할 트리 노드 */
  node: GroupTreeNodeType;
  /** 현재 들여쓰기 깊이 (루트 = 0) */
  depth: number;
  /** 현재 펼쳐진 그룹 ID 집합 */
  expandedIds: Set<number>;
  /** 접기/펼치기 토글 콜백 */
  onToggle: (id: number) => void;
  /** 현재 선택된 그룹 ID (null = 선택 없음) */
  selectedId: number | null;
  /** 노드 선택 콜백 */
  onSelect: (id: number) => void;
}

// 들여쓰기 단위 (px) — depth 당 우측 이동 폭. 좁게 유지해야 깊은 트리도 텍스트가 잘 보임.
const INDENT_PX = 10;

// ─── GroupTreeNodeComponent ───────────────────────────────────

export function GroupTreeNodeComponent({
  node,
  depth,
  expandedIds,
  onToggle,
  selectedId,
  onSelect,
}: GroupTreeNodeProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;

  // 인라인 이름 편집 상태
  const [isEditing, setIsEditing] = useState(false);
  // 하위 그룹 인라인 추가 상태 (+ 버튼 or N 단축키)
  const [isAddingChild, setIsAddingChild] = useState(false);
  // 행 focus 상태 (키보드 단축키용)
  const rowRef = useRef<HTMLDivElement>(null);

  // 훅
  const { push: toastPush } = useToast();
  const updateGroup = useUpdateGroup();
  const createGroup = useCreateGroup();
  const deleteGroup = useDeleteGroup();

  // ─── DnD — 드래그 대상 등록 ──────────────────────────────

  const draggableData: DndDraggableData = {
    type: 'group',
    groupId: node.id,
    kind: node.kind,
  };

  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `group-${node.id}`,
    data: draggableData,
  });

  // ─── DnD — 드롭 수용 대상 등록 ─────────────────────────

  const droppableData: DndDroppableData = {
    type: 'group',
    groupId: node.id,
    kind: node.kind,
  };

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `group-${node.id}`,
    data: droppableData,
  });

  // ─── DnD Ext — 800ms hover 자동 펼침 콜백 등록 ───────────

  const { registerExpandCallback } = useDndExt();

  useEffect(() => {
    registerExpandCallback((groupId: number) => {
      if (!expandedIds.has(groupId)) {
        onToggle(groupId);
      }
    });
  // registerExpandCallback은 stable ref이므로 의존성 제외
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIds, onToggle]);

  // ─── 드래그 스타일 ──────────────────────────────────────

  const dragStyle: React.CSSProperties = transform
    ? { transform: CSS.Translate.toString(transform) }
    : {};

  const nodeStyle: React.CSSProperties = {
    ...dragStyle,
    opacity: isDragging ? 0.4 : 1,
  };

  // ─── 이름 편집 저장 ─────────────────────────────────────

  const handleSaveName = useCallback(
    async (newName: string) => {
      await updateGroup.mutateAsync({ id: node.id, patch: { name: newName } });
      setIsEditing(false);
      toastPush({ title: '이름이 변경되었습니다', variant: 'success', duration: 2000 });
    },
    [node.id, updateGroup, toastPush],
  );

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  // ─── 하위 그룹 추가 ─────────────────────────────────────

  const handleAddChild = useCallback(() => {
    // 펼쳐지지 않았으면 자동 펼침
    if (!isExpanded) {
      onToggle(node.id);
    }
    setIsAddingChild(true);
  }, [isExpanded, node.id, onToggle]);

  const handleSaveChild = useCallback(
    async (name: string) => {
      await createGroup.mutateAsync({
        name,
        kind: node.kind,
        parentId: node.id,
      });
      setIsAddingChild(false);
      toastPush({ title: `'${name}' 그룹이 추가되었습니다`, variant: 'success', duration: 2000 });
    },
    [node.id, node.kind, createGroup, toastPush],
  );

  const handleCancelAddChild = useCallback(() => {
    setIsAddingChild(false);
  }, []);

  // ─── 그룹 삭제 ──────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    try {
      // groupName을 전달 → useDeleteGroup의 undo push에서 description에 사용
      await deleteGroup.mutateAsync({ id: node.id, cascade: true, groupName: node.name });
      // Undo 토스트는 useDeleteGroup의 onSuccess에서 UndoContext에 등록됨 (U53)
      // UndoContext 없을 때 폴백 토스트
    } catch {
      toastPush({ title: '삭제에 실패했습니다', variant: 'error', duration: 3000 });
    }
  }, [node.id, node.name, deleteGroup, toastPush]);

  // ─── 키보드 단축키 (행 focus 시) ───────────────────────

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // 편집/추가 중에는 단축키 무시 (InlineEditable이 직접 처리)
      if (isEditing || isAddingChild) return;

      if (e.key === 'F2') {
        e.preventDefault();
        setIsEditing(true);
      } else if (e.key === 'n' || e.key === 'N') {
        // 텍스트 입력 포커스가 아닌 경우에만
        if (document.activeElement === rowRef.current) {
          e.preventDefault();
          handleAddChild();
        }
      } else if (e.key === 'Delete') {
        if (document.activeElement === rowRef.current) {
          e.preventDefault();
          void handleDelete();
        }
      }
    },
    [isEditing, isAddingChild, handleAddChild, handleDelete],
  );

  // ─── 더블클릭 → 인라인 편집 ────────────────────────────

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  return (
    <li className="tree-node__item">
      <div
        ref={(el) => {
          // drag + drop ref 동시 연결
          setDragRef(el);
          setDropRef(el);
          (rowRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }}
        className={[
          'tree-node__row',
          isSelected ? 'tree-node__row--selected' : '',
          isOver ? 'tree-node__row--drop-target' : '',
          isDragging ? 'tree-node__row--dragging' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          ...nodeStyle,
          paddingLeft: `${depth * INDENT_PX + 8}px`,
        }}
        onKeyDown={handleKeyDown}
        // 드래그 속성은 전체 행에 적용 (편집 중이면 DnD 비활성화)
        // dragAttributes에 tabIndex가 포함되므로 별도 선언 제거
        {...(!isEditing ? dragAttributes : { tabIndex: 0 })}
        {...(!isEditing ? dragListeners : {})}
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={hasChildren ? isExpanded : undefined}
      >
        {/* ── 1. 드래그 핸들 — hover 시 표시 ── */}
        <Tooltip content="드래그로 이동" side="top" sideOffset={4} delayDuration={800}>
          <span
            className="tree-node__drag-handle"
            aria-hidden="true"
          >
            <GripVertical size={13} aria-hidden />
          </span>
        </Tooltip>

        {/* ── 2. 토글 버튼 (자식 있을 때만) ── */}
        <Tooltip
          content={hasChildren ? (isExpanded ? '접기' : '펼치기') : ''}
          side="top"
          sideOffset={4}
          delayDuration={800}
          disabled={!hasChildren}
        >
          <button
            type="button"
            className="tree-node__toggle"
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) onToggle(node.id);
            }}
            aria-label={hasChildren ? (isExpanded ? '접기' : '펼치기') : undefined}
            aria-expanded={hasChildren ? isExpanded : undefined}
            style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {hasChildren ? (
              isExpanded
                ? <ChevronDown size={13} aria-hidden className="tree-node__toggle-icon tree-node__toggle-icon--open" />
                : <ChevronRight size={13} aria-hidden className="tree-node__toggle-icon" />
            ) : null}
          </button>
        </Tooltip>

        {/* ── 3. 그룹 아이콘 ── */}
        <span className="tree-node__folder-icon" aria-hidden="true">
          {isExpanded
            ? <FolderOpen size={13} />
            : <Folder size={13} />
          }
        </span>

        {/* ── 4. 이름 영역 (flex-grow, 더블클릭 편집) ── */}
        {isEditing ? (
          <InlineEditable
            value={node.name}
            onSave={handleSaveName}
            onCancel={handleCancelEdit}
            validate={(v) => v.trim().length > 0}
            autoFocus
            className="tree-node__inline-edit"
          />
        ) : (
          <button
            type="button"
            className="tree-node__label"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(node.id);
            }}
            onDoubleClick={handleDoubleClick}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {node.icon && <span className="tree-node__icon">{node.icon}</span>}
            <span className="tree-node__name">{node.name}</span>
          </button>
        )}

        {/* ── 5. 자식 카운트 뱃지 ── */}
        {hasChildren && !isEditing && (
          <span
            className="tree-node__count"
            aria-label={`하위 ${node.children.length}개`}
          >
            {node.children.length}
          </span>
        )}

        {/* ── 6. + 버튼 (hover 시 노출) ── */}
        {!isEditing && (
          <span className="tree-node__actions">
            <Tooltip content="하위 그룹 추가 (N)" side="top" sideOffset={4} delayDuration={600}>
              <IconButton
                icon={<Plus size={12} />}
                aria-label="하위 그룹 추가"
                variant="ghost"
                size="sm"
                className="tree-node__action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddChild();
                }}
                onPointerDown={(e) => e.stopPropagation()}
              />
            </Tooltip>

            {/* ── 7. ⋯ 메뉴 ── */}
            <DropdownMenu
              trigger={
                <Tooltip content="메뉴" side="top" sideOffset={4} delayDuration={600}>
                  <IconButton
                    icon={<MoreHorizontal size={12} />}
                    aria-label="더 보기"
                    variant="ghost"
                    size="sm"
                    className="tree-node__action-btn"
                    onPointerDown={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              }
              align="end"
              sideOffset={4}
            >
              <DropdownItem
                icon={<Pencil size={13} />}
                shortcut="F2"
                onSelect={() => setIsEditing(true)}
              >
                이름 변경
              </DropdownItem>
              <DropdownItem
                icon={<Plus size={13} />}
                shortcut="N"
                onSelect={handleAddChild}
              >
                하위 그룹 추가
              </DropdownItem>
              <DropdownSeparator />
              <DropdownItem
                icon={<Trash2 size={13} />}
                shortcut="Del"
                variant="danger"
                onSelect={() => void handleDelete()}
              >
                삭제
              </DropdownItem>
            </DropdownMenu>
          </span>
        )}
      </div>

      {/* 자식 노드 재귀 렌더 — 펼쳐진 경우에만 */}
      {(hasChildren && isExpanded || isAddingChild) && (
        <ul className="tree-node__children">
          {/* 인라인 하위 그룹 추가 input */}
          {isAddingChild && (
            <li className="tree-node__item tree-node__item--adding">
              <div
                className="tree-node__row"
                style={{ paddingLeft: `${(depth + 1) * INDENT_PX + 8}px` }}
              >
                <span className="tree-node__folder-icon" aria-hidden>
                  <Folder size={13} />
                </span>
                <InlineEditable
                  value=""
                  onSave={handleSaveChild}
                  onCancel={handleCancelAddChild}
                  validate={(v) => v.trim().length > 0}
                  autoFocus
                  placeholder="새 그룹 이름 (Esc로 취소)"
                  className="tree-node__inline-edit"
                />
              </div>
            </li>
          )}
          {hasChildren && isExpanded && node.children.map((child) => (
            <GroupTreeNodeComponent
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
